// BUILD-042 simple benefits (migration 207) — the employee's own Benefits view. Shows the
// PUBLISHED benefit information (what the company offers) plus THIS employee's own eligibility.
// Read-only: an employee can see their eligibility but cannot change it, and never sees anyone
// else's. Authorized server-side by subject match (GET /hr/me/benefits).
//
// Eligibility shown here is a REVIEW OUTCOME, not enrollment — "Eligible" means HR has reviewed
// the employee as eligible, not that they are enrolled or that coverage has started.
import { useEffect, useState } from 'react';
import { getMyHrBenefits, type HrBenefitEntry, type HrBenefitEligibilityStatus } from '../../../api/client';
import { formatLocalDate } from '../hrFormat';

const STATUS_LABEL: Record<HrBenefitEligibilityStatus, string> = {
  not_reviewed: 'Not yet reviewed', eligible: 'Eligible', not_eligible: 'Not eligible',
};
const STATUS_PILL: Record<HrBenefitEligibilityStatus, string> = {
  not_reviewed: 'bg-gray-100 text-gray-600 border-gray-200',
  eligible: 'bg-green-50 text-green-700 border-green-200',
  not_eligible: 'bg-gray-100 text-gray-700 border-gray-300',
};

export function BenefitsSelfSection() {
  const [benefits, setBenefits] = useState<HrBenefitEntry[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    getMyHrBenefits()
      .then((d) => { setBenefits(d.benefits); setError(null); })
      .catch(() => { setBenefits((prev) => prev ?? []); setError('Your benefit information could not be loaded right now. Please try again.'); });
  }, []);

  return (
    <div className="space-y-3">
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-5">
        <h2 className="text-base font-semibold text-gray-900">Benefits</h2>
        <p className="text-xs text-gray-500 mt-0.5">The benefits the company offers and whether you’ve been reviewed as eligible. Being marked eligible is not the same as being enrolled.</p>
      </div>

      {error && <div className="bg-red-50 text-sm text-red-700 rounded-md px-3 py-2">{error}</div>}
      {benefits === null && !error && <p className="text-sm text-gray-400">Loading…</p>}
      {benefits !== null && benefits.length === 0 && !error && (
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-5 text-sm text-gray-500">
          No benefit information is published yet. Check back later or contact HR.
        </div>
      )}

      {(benefits ?? []).map((b) => (
        <div key={b.planId} className="bg-white rounded-lg border border-gray-200 shadow-sm p-5">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm font-semibold text-gray-900">{b.name}</span>
                {b.category && <span className="text-xs text-gray-500 border border-gray-200 rounded px-1.5 py-0.5">{b.category}</span>}
              </div>
              {b.providerName && <p className="text-xs text-gray-500 mt-0.5">{b.providerName}</p>}
            </div>
            <div className="flex-shrink-0 text-right">
              <span className={`text-xs border rounded px-1.5 py-0.5 ${STATUS_PILL[b.status]}`}>{STATUS_LABEL[b.status]}</span>
              {b.status === 'eligible' && b.eligibilityDate && <div className="text-xs text-gray-700 mt-1" data-testid="my-eligible-starting">Eligible starting {formatLocalDate(b.eligibilityDate)}</div>}
            </div>
          </div>

          {b.shortDescription && <p className="text-sm text-gray-700 mt-2 whitespace-pre-wrap">{b.shortDescription}</p>}

          {(b.providerContact || b.externalUrl || b.links.length > 0) && (
            <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-sm">
              {b.providerContact && <span className="text-gray-600">Contact: {b.providerContact}</span>}
              {b.externalUrl && <a className="text-[#4AABDE] hover:underline" href={b.externalUrl} target="_blank" rel="noreferrer">More information</a>}
              {b.links.map((l) => (
                <a key={l.linkId} className="text-[#4AABDE] hover:underline" href={l.url} target="_blank" rel="noreferrer">{l.label}</a>
              ))}
            </div>
          )}

          {b.status === 'not_reviewed' && (
            <p className="text-xs text-gray-400 mt-2">HR hasn’t recorded an eligibility decision for you on this benefit yet.</p>
          )}
        </div>
      ))}
    </div>
  );
}
